import { useState } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDate } from '@/utils/format'
import { getAppTimezoneParts } from '@/utils/date-filters'
import { cn } from '@/utils/cn'

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const

function parseYmd(value: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) }
}

function toYmd(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function monthViewFrom(value: string): { y: number; m: number } {
  const parsed = parseYmd(value)
  if (parsed) return { y: parsed.y, m: parsed.m }
  const { year, month } = getAppTimezoneParts()
  return { y: year, m: month - 1 }
}

function shiftMonth(view: { y: number; m: number }, delta: number): { y: number; m: number } {
  const date = new Date(view.y, view.m + delta, 1)
  return { y: date.getFullYear(), m: date.getMonth() }
}

export function FollowUpDateField({
  id,
  value,
  onChange,
  disabled,
  compact,
  className,
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  /** Text-like trigger for metadata rows instead of a full-width input. */
  compact?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(value)
  const [view, setView] = useState(() => monthViewFrom(value))

  // "Today" is the Asia/Karachi calendar day (same as Due today / remainingDays),
  // not the device clock's local date.
  const todayParts = getAppTimezoneParts()
  const today = toYmd(todayParts.year, todayParts.month - 1, todayParts.day)
  const monthLabel = new Date(view.y, view.m, 1).toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
  })
  const firstWeekday = (new Date(view.y, view.m, 1).getDay() + 6) % 7
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate()
  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]

  if (disabled) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center text-foreground',
          className,
        )}
      >
        {value ? formatDate(value) : '—'}
      </span>
    )
  }

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setDraft(value)
          setView(monthViewFrom(value))
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant={compact ? 'ghost' : 'outline'}
          className={cn(
            compact
              ? 'h-7 w-auto shrink-0 justify-center gap-1.5 rounded-md border border-input bg-background px-2 text-xs font-normal shadow-none hover:bg-muted/40'
              : 'h-10 w-full justify-start gap-1.5 px-3 text-sm font-normal md:h-[2.375rem]',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarDays className={cn('shrink-0 opacity-70', compact ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5')} />
          <span className="min-w-0 truncate tabular-nums">
            {value ? formatDate(value) : 'Select date'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[60] w-[min(17.5rem,calc(100vw-2rem))] p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous month"
            onClick={() => setView((current) => shiftMonth(current, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="text-sm font-medium tabular-nums">{monthLabel}</p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Next month"
            onClick={() => setView((current) => shiftMonth(current, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2 grid grid-cols-7 gap-0.5">
          {WEEKDAYS.map((label) => (
            <span
              key={label}
              className="py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {label}
            </span>
          ))}
          {cells.map((day, index) => {
            if (day == null) {
              return <span key={`empty-${index}`} />
            }
            const ymd = toYmd(view.y, view.m, day)
            const selected = draft === ymd
            return (
              <button
                key={ymd}
                type="button"
                onClick={() => setDraft(ymd)}
                className={cn(
                  'h-8 rounded-md text-xs tabular-nums transition-colors',
                  selected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                  !selected && ymd === today && 'ring-1 ring-primary/40',
                )}
              >
                {day}
              </button>
            )
          })}
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          {value ? (
            <Button
              type="button"
              variant="ghost"
              className="h-8 px-2.5 text-xs"
              onClick={() => {
                onChange('')
                setDraft('')
                setOpen(false)
              }}
            >
              Clear
            </Button>
          ) : null}
          <Button type="button" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="h-8 px-3 text-xs"
            onClick={() => {
              onChange(draft)
              setOpen(false)
            }}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
